import { useState, useMemo } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { LoadingSpinner } from '@/components/common/LoadingSpinner'
import { format, parseISO } from 'date-fns'
import {
  Activity,
  AlertTriangle,
  Settings,
  User,
  Zap,
  Upload,
  Wifi,
  WifiOff,
  Battery,
  BatteryWarning
} from 'lucide-react'
import type { Device } from '@/types/device.types'
import { useWebLogs } from '@/hooks/useWebLogs'
import type { WebLogEntry } from '@/services/webLog.service'

interface ActivityLogEntry {
  id: string
  timestamp: string
  type: 'config' | 'alert' | 'user' | 'system' | 'firmware' | 'connection' | 'battery'
  title: string
  description: string
  severity?: 'info' | 'warning' | 'error'
  user?: string
}

interface DeviceActivityLogProps {
  device: Device
}

// Map WebLogEntry to ActivityLogEntry
const mapWebLogToActivity = (log: WebLogEntry): ActivityLogEntry => {
  // Determine type based on log.type
  let type: ActivityLogEntry['type'] = 'system'
  let severity: ActivityLogEntry['severity'] = 'info'
  let title = log.type || 'System Event'
  let description = log.log_data || 'No description available'

  // Map log types to activity types
  if (log.type?.toLowerCase().includes('config')) {
    type = 'config'
    title = 'Configuration Change'
  } else if (log.type?.toLowerCase().includes('alert')) {
    type = 'alert'
    severity = 'warning'
    title = 'Alert Triggered'
  } else if (log.type?.toLowerCase().includes('battery')) {
    type = 'battery'
    severity = 'warning'
    title = 'Battery Event'
  } else if (log.type?.toLowerCase().includes('connect') || log.type?.toLowerCase().includes('transmission')) {
    type = 'connection'
    title = log.type.includes('fail') ? 'Connection Failed' : 'Connection Event'
    severity = log.type.includes('fail') ? 'error' : 'info'
  } else if (log.type?.toLowerCase().includes('firmware') || log.type?.toLowerCase().includes('update')) {
    type = 'firmware'
    title = 'Firmware Update'
  } else if (log.type?.toLowerCase().includes('user') || log.type?.toLowerCase().includes('edit')) {
    type = 'user'
    title = 'User Action'
  }

  return {
    id: log.id,
    timestamp: log.log_at,
    type,
    title,
    description,
    severity,
    user: log.logged_by_user?.display_name || log.user?.display_name || log.log_by
  }
}

const getActivityIcon = (type: ActivityLogEntry['type']) => {
  switch (type) {
    case 'config':
      return <Settings className="h-4 w-4" />
    case 'alert':
      return <AlertTriangle className="h-4 w-4" />
    case 'user':
      return <User className="h-4 w-4" />
    case 'system':
      return <Zap className="h-4 w-4" />
    case 'firmware':
      return <Upload className="h-4 w-4" />
    case 'connection':
      return <Wifi className="h-4 w-4" />
    case 'battery':
      return <Battery className="h-4 w-4" />
    default:
      return <Activity className="h-4 w-4" />
  }
}

const getSeverityColor = (severity?: string) => {
  switch (severity) {
    case 'error':
      return 'destructive'
    case 'warning':
      return 'default'
    case 'info':
    default:
      return 'secondary'
  }
}

export const DeviceActivityLog = ({ device }: DeviceActivityLogProps) => {
  const [filterType, setFilterType] = useState<string>('all')

  // Fetch web logs for this device
  const { data: webLogs, isLoading } = useWebLogs({
    device_id: device.id,
    days: 30, // Last 30 days
    page_size: 100
  })

  // Map web logs to activity log entries
  const activities = useMemo(() => {
    if (!webLogs) return []
    return webLogs.map(mapWebLogToActivity).sort((a, b) => {
      // Sort by timestamp descending (newest first)
      return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    })
  }, [webLogs])

  const filteredActivities = useMemo(() => {
    if (filterType === 'all') return activities
    return activities.filter(activity => activity.type === filterType)
  }, [activities, filterType])

  if (isLoading) {
    return <LoadingSpinner />
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Activity className="h-5 w-5" />
                Activity Log
              </CardTitle>
              <CardDescription>
                Chronological history of device events and changes
              </CardDescription>
            </div>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Events</SelectItem>
                <SelectItem value="connection">Connection</SelectItem>
                <SelectItem value="alert">Alerts</SelectItem>
                <SelectItem value="battery">Battery</SelectItem>
                <SelectItem value="config">Configuration</SelectItem>
                <SelectItem value="user">User Actions</SelectItem>
                <SelectItem value="system">System</SelectItem>
                <SelectItem value="firmware">Firmware</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {filteredActivities.length > 0 ? (
            <div className="space-y-4 max-h-[600px] overflow-y-auto">
              {filteredActivities.map((activity, index) => (
                <div
                  key={activity.id}
                  className="flex gap-4 pb-4 border-b last:border-0"
                >
                  {/* Timeline indicator */}
                  <div className="flex flex-col items-center">
                    <div className="flex h-9 w-9 items-center justify-center rounded-full border-2 bg-background">
                      {getActivityIcon(activity.type)}
                    </div>
                    {index < filteredActivities.length - 1 && (
                      <div className="w-px h-full bg-border mt-2" />
                    )}
                  </div>

                  {/* Activity content */}
                  <div className="flex-1 space-y-1">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <h4 className="text-sm font-semibold">{activity.title}</h4>
                        <Badge variant={getSeverityColor(activity.severity)}>
                          {activity.type}
                        </Badge>
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {format(parseISO(activity.timestamp), 'MMM d, yyyy HH:mm')}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {activity.description}
                    </p>
                    {activity.user && (
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        <User className="h-3 w-3" />
                        {activity.user}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex items-center justify-center h-[200px] text-muted-foreground">
              No activity log entries found
            </div>
          )}
        </CardContent>
      </Card>

      {/* Summary Statistics */}
      <Card>
        <CardHeader>
          <CardTitle>Activity Summary (Last 30 Days)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="space-y-1">
              <p className="text-sm font-medium text-muted-foreground">Total Events</p>
              <p className="text-2xl font-bold">{activities.length}</p>
            </div>
            <div className="space-y-1">
              <p className="text-sm font-medium text-muted-foreground">Alerts</p>
              <p className="text-2xl font-bold text-yellow-600">
                {activities.filter(a => a.type === 'alert').length}
              </p>
            </div>
            <div className="space-y-1">
              <p className="text-sm font-medium text-muted-foreground">Connections</p>
              <p className="text-2xl font-bold text-green-600">
                {activities.filter(a => a.type === 'connection' && a.severity !== 'error').length}
              </p>
            </div>
            <div className="space-y-1">
              <p className="text-sm font-medium text-muted-foreground">Errors</p>
              <p className="text-2xl font-bold text-red-600">
                {activities.filter(a => a.severity === 'error').length}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
